import { useEffect, useRef, useState } from 'react';

/** The one length everything in this app leaves over. See «Motion». */
export const LEAVING_MS = 180;

type Key = string | number;

/**
 * The list as it has to be drawn, given what it was and what it now is.
 *
 * Whoever was drawn last time and is not in the list any more starts leaving,
 * from wherever they were standing when it happened, and is put back at that
 * place until somebody says they are done. Everything else is the list itself.
 *
 * A function of its arguments and nothing else, so that the bookkeeping - where
 * a leaver goes back, what happens when two of them leave at once, what happens
 * when one comes back - can be read and tested without a browser. `useLeaving`
 * below is this plus a clock.
 */
export function standing<T>(
  /** What was drawn last time, leavers included. */
  drawn: readonly T[],
  /** What the list is now. */
  items: readonly T[],
  /** Who is already leaving, and which place they were standing at. */
  going: ReadonlyMap<Key, number>,
  keyOf: (item: T) => Key,
): { rows: T[]; going: Map<Key, number>; away: Set<Key> } {
  const here = new Map(items.map((one) => [keyOf(one), one]));

  const next = new Map(going);
  drawn.forEach((one, at) => {
    const key = keyOf(one);
    if (!here.has(key) && !next.has(key)) next.set(key, at);
  });

  /**
   * The ones that are actually still going. A row can come back — an undo bar
   * re-reads rather than reverses — and then it is in the list again while its
   * own run is still counting down, so it must be drawn once and drawn whole.
   */
  const away = new Set<Key>();
  next.forEach((_, key) => {
    if (!here.has(key)) away.add(key);
  });

  const held = new Map(drawn.map((one) => [keyOf(one), one]));
  const rows = [...items];
  // Lowest place first, so each one lands where it stood rather than being
  // pushed along by the one after it.
  [...next.entries()]
    .filter(([key]) => away.has(key))
    .sort((a, b) => a[1] - b[1])
    .forEach(([key, at]) => {
      const one = held.get(key);
      if (one !== undefined) rows.splice(Math.min(at, rows.length), 0, one);
    });

  return { rows, going: next, away };
}

/**
 * What is left of the two once a row's run has ended.
 *
 * Both halves, and the second is the one that is easy to forget: `standing`
 * decides who is leaving by comparing what was drawn with what the list is, so
 * a row taken out of `going` and left in `drawn` is found missing all over
 * again on the very next render. It then leaves a second time, and a third,
 * for ever — at nought pixels tall, where nobody would ever see it and the
 * timers would go on being made.
 */
export function forgotten<T>(
  drawn: readonly T[],
  going: ReadonlyMap<Key, number>,
  key: Key,
  keyOf: (item: T) => Key,
): { drawn: T[]; going: Map<Key, number> } {
  const next = new Map(going);
  next.delete(key);
  return { drawn: drawn.filter((one) => keyOf(one) !== key), going: next };
}

/**
 * A list that lets a row close before it takes it out.
 *
 * The app animated every arrival and no departure at all, which is the
 * asymmetry `Unfold` was cured of on 2026-08-21 and which every list still had:
 * a row answered «Нет» was in the DOM in one frame and gone in the next, and the
 * rows under it jumped up by its height under the finger that had just pressed
 * it.
 *
 * The reason no list had it is that a row which has left the array has nothing
 * left to animate — the same thing `Unfold` and `Sheet` both solve by outliving
 * the decision that closed them. This is that, for a list rather than a panel.
 *
 * By difference and not by the caller saying «remove this one», because the two
 * lists that need it lose their rows in different ways: the requests are
 * filtered out on the spot, and the programme is replaced wholesale by the
 * answer the server sends back. Both look the same from here — a key that was
 * in the list and is not any more.
 *
 * The rebuilding is done during the render and not in an effect, and that is
 * load-bearing rather than a shortcut. The row has to be drawn once more
 * **without** the leaving class, on the same DOM node it already had, or there
 * is no previous height for the transition to run from; an effect would put it
 * back a frame after React had already taken it out. `Fade.tsx` computes in the
 * render for the same kind of reason.
 */
export default function useLeaving<T>(
  items: readonly T[],
  keyOf: (item: T) => Key,
  ms: number = LEAVING_MS,
): { rows: readonly T[]; leaving: (item: T) => boolean } {
  const drawn = useRef<T[]>([...items]);
  const going = useRef<Map<Key, number>>(new Map());
  const timers = useRef<Map<Key, number>>(new Map());
  const [, redraw] = useState(0);

  const now = standing(drawn.current, items, going.current, keyOf);
  going.current = now.going;
  drawn.current = now.rows;

  // On every render, because what starts a run is worked out in the render
  // above and there is nothing to depend on that says «a new one started».
  useEffect(() => {
    going.current.forEach((_, key) => {
      if (timers.current.has(key)) return;
      timers.current.set(
        key,
        window.setTimeout(() => {
          timers.current.delete(key);
          const after = forgotten(drawn.current, going.current, key, keyOf);
          drawn.current = after.drawn;
          going.current = after.going;
          redraw((n) => n + 1);
        }, ms),
      );
    });
  });

  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
    },
    [],
  );

  return { rows: now.rows, leaving: (one: T) => now.away.has(keyOf(one)) };
}
